import React, { useEffect, useState, useRef } from "react";
import { useCurrentTile } from "../../contexts/CurrentTileContext";

/**
 * HTML Overlay component that displays project details
 * Slides up when a project is in view, slides down when scrolling away
 * Properly animates transitions between different projects
 */
const ProjectOverlay = () => {
  const { currentTile, isInProjectsZone } = useCurrentTile();
  const [displayTile, setDisplayTile] = useState(null); // Tile currently displayed
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const prevTileRef = useRef(null);
  const transitionTimeoutRef = useRef(null);

  useEffect(() => {
    // Clear any pending timeouts
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    if (currentTile && isInProjectsZone) {
      // Check if this is a different tile than what's currently displayed
      const currentDisplayId = displayTile?.id;
      const newTileId = currentTile.id;
      const isDifferentTile = currentDisplayId && currentDisplayId !== newTileId;
      const isFirstTime = !displayTile;

      if (isDifferentTile) {
        // Different tile - animate transition
        // First, slide down the old overlay (keep old content visible)
        setIsVisible(false);
        
        // After slide down completes, update content and slide up new overlay
        transitionTimeoutRef.current = setTimeout(() => {
          setDisplayTile(currentTile);
          prevTileRef.current = currentTile;
          // Small delay to ensure render before animation
          setTimeout(() => setIsVisible(true), 5);
        }, 250); // Reduced from 500ms to 250ms
      } else if (isFirstTime) {
        // First time showing - initialize and show immediately
        setShouldRender(true);
        setDisplayTile(currentTile);
        prevTileRef.current = currentTile;
        // Very small delay to ensure render before animation
        setTimeout(() => setIsVisible(true), 5);
      }
      // If same tile, do nothing (keep current state)
    } else {
      // Scrolling away - slide down and remove
      if (shouldRender && displayTile) {
        setIsVisible(false);
        transitionTimeoutRef.current = setTimeout(() => {
          setShouldRender(false);
          setDisplayTile(null);
          prevTileRef.current = null;
        }, 250); // Reduced from 500ms to 250ms
      }
    }

    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
    // Only depend on currentTile and isInProjectsZone to avoid re-triggering on displayTile changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTile, isInProjectsZone]);

  if (!shouldRender || !displayTile) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-15 left-4 right-4 md:left-10 md:bottom-10 md:right-auto z-50 transition-all ease-in-out ${
        isVisible 
          ? "translate-y-0 opacity-100" 
          : "translate-y-full opacity-0"
      }`}
      style={{
        fontFamily: "Modeka, sans-serif",
        transitionDuration: "250ms",
      }}
      key={displayTile.id} // Key ensures content updates properly
    >
      <div className="bg-black/80 backdrop-blur-md p-4 md:p-6 border-2 border-white shadow-2xl flex flex-col justify-between h-full min-h-20 w-full md:max-w-90">
        {/* Description */}
        {displayTile.description && (
          <p className="text-white text-2xl md:text-lg mb-2">
            {displayTile.description}
          </p>
        )}

        {/* Tech Stack */}
        {displayTile.tech && displayTile.tech.length > 0 && (
          <div className="mt-2">
            <div className="flex flex-wrap gap-2">
              {displayTile.tech.map((tech, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-white/10 text-white text-sm md:text-[0.7rem]  rounded-xl border border-white"
                  style={{ fontFamily: "Azonix, sans-serif" }}
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ProjectOverlay;

